import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Starfield from "@/components/Starfield";
import Rocket from "@/components/Rocket";
import { getTeacherInvite, acceptTeacherInvite } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function TeacherInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTeacherInvite(token);
        setState({ loading: false, ...data });
      } catch {
        setState({ loading: false, notFound: true });
      }
    })();
  }, [token]);

  const accept = async () => {
    const n = name.trim();
    if (!n) {
      toast.error("Please enter your name");
      return;
    }
    setBusy(true);
    try {
      const data = await acceptTeacherInvite(token, n);
      setResult(data);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 410 ? "This invite is no longer valid" : "Could not join as teacher");
    } finally {
      setBusy(false);
    }
  };

  const invalid = state.notFound || state.used || state.expired;

  return (
    <div className="relative min-h-screen bg-[#0B0D1B] text-white overflow-hidden flex items-center justify-center p-4">
      <Starfield />
      <div className="relative z-10 w-full max-w-md bg-[#141738] border border-[#252B5C] rounded-3xl p-7 text-center">
        <div className="rocket-float inline-block mb-3">
          <Rocket color="#FFD166" size={64} />
        </div>

        {state.loading ? (
          <p data-testid="invite-loading" className="text-slate-300 flex items-center justify-center gap-2 py-6">
            <Loader2 className="w-5 h-5 animate-spin" /> Checking invite…
          </p>
        ) : result ? (
          <div data-testid="invite-success">
            <h1 className="font-heading text-2xl font-bold text-amber-300 mb-1">You're in, {result.teacherName}! 👑</h1>
            <p className="text-slate-400 mb-4">
              You now have teacher access to <span className="text-cyan-300 font-semibold">{result.className}</span>.
            </p>
            <p className="text-sm text-slate-400 mb-2">Your personal teacher code (keep it safe — sign in with it any time):</p>
            <div
              data-testid="invite-new-code"
              className="font-heading font-bold text-5xl tracking-[0.4em] text-amber-300 bg-[#0E1126] border-2 border-amber-400/40 rounded-2xl py-6 mb-5"
            >
              {result.teacherCode}
            </div>
            <Button
              data-testid="invite-open-board-btn"
              onClick={() => navigate(`/class/${result.classId}`)}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]"
            >
              <Rocket className="w-4 h-4 mr-2" /> Open the board
            </Button>
          </div>
        ) : invalid ? (
          <div data-testid="invite-invalid">
            <h1 className="font-heading text-2xl font-bold text-red-300 mb-2">Invite unavailable</h1>
            <p className="text-slate-400 mb-5">
              {state.notFound
                ? "This co-teacher invite link is not valid."
                : state.used
                ? "This invite has already been used. Ask the class teacher for a new one."
                : "This invite has expired. Ask the class teacher for a new one."}
            </p>
            <Button
              data-testid="invite-home-btn"
              onClick={() => navigate("/")}
              variant="outline"
              className="border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B] min-h-[44px]"
            >
              Go home
            </Button>
          </div>
        ) : (
          <div data-testid="invite-accept-form">
            <h1 className="font-heading text-2xl font-bold text-cyan-300 mb-1 flex items-center justify-center gap-2">
              <ShieldCheck className="w-6 h-6" /> Co-teacher invite
            </h1>
            <p className="text-slate-400 mb-5">
              You've been invited to co-teach <span className="text-cyan-300 font-semibold">{state.className}</span>. Enter your
              name to get your own teacher code and full access.
            </p>
            <Input
              data-testid="invite-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && accept()}
              placeholder="e.g. Mr. Okafor"
              className="bg-[#0E1126] border-[#252B5C] text-white min-h-[48px] text-lg mb-4"
              autoFocus
            />
            <Button
              data-testid="invite-accept-btn"
              onClick={accept}
              disabled={busy}
              className="w-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-white font-heading font-bold min-h-[48px]"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join as co-teacher 🚀"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
